import { AuthenticateResult, UserProfile } from '@feedyou/schema'
import { createSelector } from 'reselect'

import { RootState } from '../../reducers'
import { ConnectedSocketUser } from '../socketServer/types'
import { AccessLevel, UserAuthInfo } from './types'
import { SocketLog } from '../../lib/debugging/socketLog'
import { endpointHelpers } from '../../features/serverBots/endpointHelpers'
import { MINIMUM_PERMISSIONS } from '../../features/serverBots/constants'
import { expandRole } from './utils/expandRole'
import { lockedStatePermissionSettingsSelector } from '../app/selectors/lockedStateSelector'

export function userInfoSelector(state: RootState): UserAuthInfo {
  return state.user.info
}

export function userPermissionsSelector(state: RootState): AuthenticateResult {
  return (
    endpointHelpers.getCurrentUserPermissionsForCurrentBot.selectData()(state) ??
    MINIMUM_PERMISSIONS
  )
}

export function deployPermissionUsersForCurrentBotSelector(state: RootState) {
  return userPermissionsSelector(state).usersWithDeployPermission
}

export function isAnonymousUserSelector(state: RootState): boolean {
  return !userInfoSelector(state).id
}

export const connectedUserSelector = createSelector(
  [userInfoSelector, userPermissionsSelector],
  (user, permissions): ConnectedSocketUser => {
    const { id, email, name, picture } = user
    const { role } = permissions

    return { id, email, name, picture, role }
  }
)

export const authorForCurrentUserSelector = createSelector(userInfoSelector, (user: UserAuthInfo):
  | UserProfile
  | undefined => {
  const { id, name = 'You', email = '', picture } = user

  if (!id) {
    return
  }

  return { id, name, email, picture }
})

export const accessTokenSelector = (state: RootState) => {
  return state.user.info.accessToken
}

export const expandedUserPermissionsSelector = createSelector(
  (state: RootState) => userInfoSelector(state),
  (state: RootState) => userPermissionsSelector(state),
  (user, nativeUserPermissions): AccessLevel => {
    return {
      isLoggedIn: !!user.accessToken,
      tokenLogin: !!user.tokenLogin,
      verifiedEmail: user.tokenLogin ? true : !!user.verifiedEmail,
      ...nativeUserPermissions,
      // @ts-ignore
      ...expandRole(nativeUserPermissions.role)
    }
  }
)

export const accessLevelSelector = createSelector(
  (state: RootState) => expandedUserPermissionsSelector(state),
  (state: RootState) => lockedStatePermissionSettingsSelector(state),
  (expandedUserPermissions, lockedStatePermissionSettings): AccessLevel => {
    SocketLog.log('Notice', 'accessLevelSelector', {
      expandedUserPermissions,
      lockedStatePermissionSettings
    })

    return {
      ...expandedUserPermissions,
      ...lockedStatePermissionSettings
    }
  }
)
